import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {HttpClient, HttpParams} from '@angular/common/http';
import {ApiService, EnvironmentModel, GenericMessage, GenericPackagedCountResponse, GenericPair, ObservableData} from '@cas-hub/acabim-common-client';
import {ContactTypeRequest, ContactTypeResponse, ContactTypesResponse, GroupedRolesResponse} from '../_models/contact-type.model';

@Injectable({
    providedIn: 'root'
})
export class ContactTypeService extends ApiService {

    constructor(protected client: HttpClient, protected environment: EnvironmentModel) {
        super(client, environment);
    }

    protected controller = 'contact-type';

    /**
     * Add a new or update an existing contact type
     * @returns A contact type object
     */
    public upsertContactType(name: string, description: string, status: number, roles: number[], id?: string) {
        const body: ContactTypeRequest = {
            name,
            description,
            status,
            roles
        };
        const endpoint = id ? id : '';
        return this.client.post(this.url(endpoint), body, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => item as ContactTypeResponse));
    }

    /**
     * Retrieve a single contact types
     * @returns Contact type details
     */
    public getContactType(id: number): Observable<ContactTypeResponse> {
        return this.client.get(this.url(id.toString()), {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => {
            return item as ContactTypeResponse;
        }));
    }

    /**
     * Delete a contact types
     * @returns Accepted
     */
    public deleteContactType(id: number): Observable<boolean> {
        return this.client.delete(this.url(id.toString()), {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => {
            return true;
        }));
    }

    /**
     * Retrieve all contact types
     * @returns An array of contact types
     */
    public getContactTypes(archived: boolean = false): Observable<ObservableData<ContactTypeResponse>> {
        let params = new HttpParams();
        if (archived) {
            params = params.set('archived', '1');
        }

        return this.client.get(this.url(''), {
            params,
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(data => ({
            totalRecords: (data as ContactTypesResponse).count,
            data: (data as ContactTypesResponse).data
        })));
    }

    /**
     * Retrieve all available CAS roles
     * @returns An array of CAS roles
     */
    public getRoles(): Observable<GroupedRolesResponse> {
        return this.client.get(this.url('role', true), {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(
            map(data => ({
            count: (data as GroupedRolesResponse).count,
            data: (data as GroupedRolesResponse).data
        })));
    }

    /**
     * Retrieve all contacts for a contact type
     * @returns An array of contact display names and ids with a count
     */
    public getContactTypeContacts(contactTypeId: number): Observable<GenericPackagedCountResponse<GenericPair>> {
        return this.client.get(
            this.url(`contact-type/${contactTypeId}/contacts`, true),
            {
                headers: this.defaultHeaders,
                responseType: 'json'
            }
        ).pipe(
            map(data => data as GenericPackagedCountResponse<GenericPair>));
    }

    /**
     * Migrate all contacts for a contact type to another contact type
     * @returns A tally of the number of contacts successfully migrated
     */
    public migrateContactTypeContacts(fromContactTypeId: number, toContactTypeId: number): Observable<GenericMessage> {
        return this.client.post<GenericMessage>(
            this.url(`contact-type/migrate/${fromContactTypeId}/${toContactTypeId}`, true),
            {
                headers: this.defaultHeaders,
                responseType: 'json'
            }
        ).pipe(
            map(data => data))
    }

    // contact-type/{contactTypeId}
    /**
     * Archive a contact type
     * @returns A tally of the number of contacts successfully migrated
     */
    public archiveContactType(contactTypeId: number): Observable<GenericMessage> {
        return this.client.delete<GenericMessage>(
            this.url(`contact-type/${contactTypeId}`, true),
            {
                headers: this.defaultHeaders,
                responseType: 'json'
            }
        ).pipe(
            map(data => data as GenericMessage))
    }
}
